import { BenchmarkTypeOf } from "@/modules/benchmark/benchmarkTypes"
import { ChronoDtoSchema } from "@/modules/chrono/services/chronoDtos"
import { UserDtoSchema } from "@/modules/profile/userProfile/userDto"
import { ScoreboardScoreDtoSchema } from "@/modules/scores/scoreboard/gateway/scoreboardDto"
import { ScoreResultType, ScoreType } from "@/modules/scores/scoreTypes"
import {
  ExerciseCategories,
  ProgramExerciseTypeOf,
  UserExerciseTypeOf,
} from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseTypes"
import {
  MediasContentDtoSchema,
  SessionMediasDtoSchema,
} from "@/modules/sessions/sessionDetails/blocks/mediaBlock/mediaBlockDto"
import { NoteType } from "@/modules/sessions/sessionDetails/blocks/noteBlock/noteTypes"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { z } from "zod"

export const CoachNoteDtoSchema = z
  .object({
    orderIndex: z.number(),
    id: z.string(),
    note: z.string(),
    noteType: z.enum(NoteType),
    durationInMin: z.number().nullable(),
  })
  .meta({ id: "CoachNoteDtoSchema" })

export type CoachNoteDto = z.infer<typeof CoachNoteDtoSchema>

export const CoachNoteInputDtoSchema = z
  .object({
    orderIndex: z.number(),
    id: z.string().nullable(),
    note: z.string(),
    noteType: z.enum(NoteType),
    durationInMin: z.number().nullable(),
  })
  .meta({ id: "CoachNoteInputDtoSchema" })
export type CoachNoteInputDto = z.infer<typeof CoachNoteInputDtoSchema>

export const AthleteNoteDtoSchema = z
  .object({
    orderIndex: z.number(),
    id: z.string(),
    note: z.string(),
  })
  .meta({ id: "AthleteNoteDtoSchema" })

export type AthleteNoteDto = z.infer<typeof AthleteNoteDtoSchema>

export const AthleteNoteInputDtoSchema = z
  .object({
    orderIndex: z.number(),
    id: z.string().nullable(),
    note: z.string(),
  })
  .meta({ id: "AthleteNoteInputDtoSchema" })
export type AthleteNoteInputDto = z.infer<typeof AthleteNoteInputDtoSchema>

export const ScoreSetConfigurationDtoSchema = z
  .object({
    id: z.string(),
    displayOrder: z.number(),
    scoreType: z.enum(ScoreType),
    useForScoring: z.boolean(),
  })
  .meta({ id: "ScoreSetConfigurationDtoSchema" })

export type ScoreSetConfigurationDto = z.infer<typeof ScoreSetConfigurationDtoSchema>

export const ScoreSetConfigurationInputDtoSchema = z
  .object({
    id: z.string().nullable(),
    displayOrder: z.number(),
    scoreType: z.enum(ScoreType),
    useForScoring: z.boolean(),
  })
  .meta({ id: "ScoreSetConfigurationInputDtoSchema" })

export type ScoreSetConfigurationInputDto = z.infer<typeof ScoreSetConfigurationInputDtoSchema>

export const ExerciseDtoSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    category: z.enum(ExerciseCategories),
    otherCategory: z.string().nullable(),
    chrono: ChronoDtoSchema.nullable(),
    isFavorite: z.boolean(),
    type: z.union([z.literal(ProgramExerciseTypeOf), z.literal(UserExerciseTypeOf), z.literal(BenchmarkTypeOf)]),
    owner: UserDtoSchema.nullable(),
    rpe: z.number().nullable(),
    requiredMedia: z.boolean(),
    durationInMin: z.number().nullable(),
    orderIndex: z.number(),
    isCompleted: z.boolean(),
    scoreType: z.enum(ScoreType), // deprecated
    score: ScoreboardScoreDtoSchema.nullable(),
    scoreSetConfigurations: z.array(ScoreSetConfigurationDtoSchema),
    scoringOption: z.enum(ScoreResultType).nullable(),
  })
  .meta({ id: "ExerciseDtoSchema" })

export type ExerciseDto = z.infer<typeof ExerciseDtoSchema>

export const ExerciseInputDtoSchema = z
  .object({
    id: z.string().nullable(),
    title: z.string(),
    description: z.string(),
    category: z.enum(ExerciseCategories),
    otherCategory: z.string().nullable(),
    chrono: ChronoDtoSchema.nullable(),
    scoreType: z.string(), // deprecated
    scoringOption: z.enum(ScoreResultType).nullable(),
    scoreSetConfigurations: z.array(ScoreSetConfigurationInputDtoSchema).nullable(),
    rpe: z.number().nullable(),
    requiredMedia: z.boolean(),
    durationInMin: z.number().nullable(),
    orderIndex: z.number(),
  })
  .meta({ id: "ExerciseInputDtoSchema" })

export type ExerciseInputDto = z.infer<typeof ExerciseInputDtoSchema>

export const SessionBlockDtoSchema = z
  .object({
    type: z.literal(SessionBlockType.COACH_NOTE),
    content: CoachNoteDtoSchema,
  })
  .or(
    z.object({
      type: z.literal(SessionBlockType.SESSION_EXERCISE),
      content: ExerciseDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.ATHLETE_NOTE),
      content: AthleteNoteDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.MEDIAS),
      content: SessionMediasDtoSchema,
    }),
  )
  .meta({ id: "SessionBlockDtoSchema" })

export type SessionBlockDto = z.infer<typeof SessionBlockDtoSchema>

export const BlockSessionInputDtoSchema = z
  .object({
    type: z.literal(SessionBlockType.COACH_NOTE),
    content: CoachNoteInputDtoSchema,
  })
  .or(
    z.object({
      type: z.literal(SessionBlockType.SESSION_EXERCISE),
      content: ExerciseInputDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.ATHLETE_NOTE),
      content: AthleteNoteInputDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.MEDIAS),
      content: MediasContentDtoSchema,
    }),
  )
  .meta({ id: "BlockSessionInputDtoSchema" })

export type BlockSessionInputDto = z.infer<typeof BlockSessionInputDtoSchema>
